import React, { useEffect } from 'react';
import {
  PreviewAssociadoSga,
  PreviewVeiculoSga,
} from '../../../api/sga-importacao/sgaImportacaoApi';
import './ModalImportacaoSga.css';

/**
 * Confirmacao de importacao de um registro do SGA (at-32).
 *
 * Reutilizado pelas duas portas de entrada (painel de vistoria e substituicao
 * de veiculo/titular), por isso e PRESENTACIONAL: quem busca o preview e faz a
 * importacao e a tela; aqui so mostramos o que veio e pedimos a confirmacao.
 *
 * Segue o padrao de overlay do `ModalConfirmacao` (at-24): Escape e clique fora
 * fecham, salvo enquanto estiver processando.
 */

export type TipoImportacao = 'ASSOCIADO' | 'VEICULO';

export interface ModalImportacaoSgaProps {
  aberto: boolean;
  tipo: TipoImportacao;
  /** Preview ja carregado; `null` enquanto a busca no SGA nao voltou. */
  preview: PreviewAssociadoSga | PreviewVeiculoSga | null;
  carregando?: boolean;
  importando?: boolean;
  onConfirmar: () => void;
  onCancelar: () => void;
}

const formatarCpf = (cpf: string): string => {
  const d = (cpf || '').replace(/\D/g, '');
  if (d.length !== 11) return cpf || '';
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

const formatarMoeda = (valor: number | null): string => {
  if (valor === null || valor === undefined || !Number.isFinite(valor)) return '-';
  return `R$ ${valor.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/** Data do SGA vem como "2026-07-21". */
const formatarData = (data: string | null): string => {
  if (!data) return '';
  const partes = data.split('-');
  if (partes.length !== 3) return data;
  return `${partes[2]}/${partes[1]}/${partes[0]}`;
};

const ehPreviewVeiculo = (
  preview: PreviewAssociadoSga | PreviewVeiculoSga,
): preview is PreviewVeiculoSga => 'placa' in preview;

const ModalImportacaoSga: React.FC<ModalImportacaoSgaProps> = ({
  aberto,
  tipo,
  preview,
  carregando = false,
  importando = false,
  onConfirmar,
  onCancelar,
}) => {
  useEffect(() => {
    if (!aberto) return undefined;

    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape' && !importando) onCancelar();
    };

    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [aberto, importando, onCancelar]);

  if (!aberto) return null;

  const carteira = preview?.carteira;
  const bloqueado = !!preview && !carteira?.podeImportar;
  const temBloqueantes = !!preview?.bloqueantes?.length;
  const podeConfirmar = !!preview && !bloqueado && !temBloqueantes && !importando && !carregando;

  const titulo =
    tipo === 'VEICULO' ? 'Importar veiculo do SGA' : 'Importar associado do SGA';

  return (
    <div
      className="importacao-sga-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
      onClick={() => {
        if (!importando) onCancelar();
      }}
    >
      <div className="importacao-sga-modal" onClick={(e) => e.stopPropagation()}>
        <div className="importacao-sga-titulo">{titulo}</div>

        {carregando && <div className="importacao-sga-status">Consultando o SGA...</div>}

        {!carregando && !preview && (
          <div className="importacao-sga-status">Nenhum dado retornado pelo SGA.</div>
        )}

        {!carregando && preview && (
          <>
            {ehPreviewVeiculo(preview) ? (
              <>
                <div className="importacao-sga-secao">
                  <div className="importacao-sga-secao-titulo">Veiculo</div>
                  <div className="importacao-sga-linha">
                    <span>Placa</span>
                    <strong>{preview.placa}</strong>
                  </div>
                  <div className="importacao-sga-linha">
                    <span>Modelo</span>
                    <strong>
                      {[preview.marca, preview.modelo].filter(Boolean).join(' ') || '-'}
                    </strong>
                  </div>
                  {preview.anoModelo ? (
                    <div className="importacao-sga-linha">
                      <span>Ano</span>
                      <strong>{preview.anoModelo}</strong>
                    </div>
                  ) : null}
                  <div className="importacao-sga-linha">
                    <span>Valor FIPE</span>
                    <strong>{formatarMoeda(preview.valorFipe)}</strong>
                  </div>
                  <div className="importacao-sga-linha">
                    <span>Valor protegido</span>
                    <strong>{formatarMoeda(preview.valorProtegido)}</strong>
                  </div>
                  {preview.situacaoSga && (
                    <div className="importacao-sga-linha">
                      <span>Situacao no SGA</span>
                      <strong>{preview.situacaoSga}</strong>
                    </div>
                  )}
                </div>

                <div className="importacao-sga-secao">
                  <div className="importacao-sga-secao-titulo">Titular</div>
                  <div className="importacao-sga-linha">
                    <span>Nome</span>
                    <strong>{preview.dono.nomeCompleto || '-'}</strong>
                  </div>
                  <div className="importacao-sga-linha">
                    <span>CPF</span>
                    <strong>{formatarCpf(preview.dono.cpfCnpj)}</strong>
                  </div>
                  {preview.dono.jaExisteLocalmente && (
                    <div className="importacao-sga-nota">
                      Este titular ja existe no SAAC — os dados dele nao serao alterados.
                    </div>
                  )}
                </div>

                {preview.jaExisteLocalmente && (
                  <div className="importacao-sga-aviso importacao-sga-aviso-neutro">
                    Este veiculo ja existe no SAAC. A importacao apenas vincula os codigos
                    do SGA que estiverem faltando — nenhum dado sera sobrescrito.
                  </div>
                )}

                {preview.vistoriasNoSga && preview.vistoriasNoSga.total > 0 && (
                  <div className="importacao-sga-aviso importacao-sga-aviso-atencao">
                    Este veiculo ja tem {preview.vistoriasNoSga.total} vistoria(s) no SGA
                    {preview.vistoriasNoSga.ultimaData
                      ? ` (ultima em ${formatarData(preview.vistoriasNoSga.ultimaData)})`
                      : ''}
                    . Confirme se e mesmo necessario fazer outra.
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="importacao-sga-secao">
                  <div className="importacao-sga-secao-titulo">Associado</div>
                  <div className="importacao-sga-linha">
                    <span>Nome</span>
                    <strong>{preview.nomeCompleto || '-'}</strong>
                  </div>
                  <div className="importacao-sga-linha">
                    <span>CPF</span>
                    <strong>{formatarCpf(preview.cpfCnpj)}</strong>
                  </div>
                  {preview.cidadeEstado && (
                    <div className="importacao-sga-linha">
                      <span>Cidade</span>
                      <strong>{preview.cidadeEstado}</strong>
                    </div>
                  )}
                  {preview.situacaoSga && (
                    <div className="importacao-sga-linha">
                      <span>Situacao no SGA</span>
                      <strong>{preview.situacaoSga}</strong>
                    </div>
                  )}
                </div>

                <div className="importacao-sga-secao">
                  <div className="importacao-sga-secao-titulo">
                    Veiculos no SGA ({preview.veiculos.length})
                  </div>
                  {preview.veiculos.length === 0 && (
                    <div className="importacao-sga-nota">
                      Este associado nao tem veiculos no SGA.
                    </div>
                  )}
                  {preview.veiculos.map((veiculo) => (
                    <div key={veiculo.placa} className="importacao-sga-veiculo">
                      <strong>{veiculo.placa}</strong>
                      <span>{veiculo.modelo || '-'}</span>
                      {veiculo.jaExisteLocalmente && (
                        <span className="importacao-sga-tag">ja no SAAC</span>
                      )}
                    </div>
                  ))}
                </div>

                {preview.jaExisteLocalmente && (
                  <div className="importacao-sga-aviso importacao-sga-aviso-neutro">
                    Este associado ja existe no SAAC. A importacao apenas vincula os codigos
                    do SGA que estiverem faltando — nenhum dado sera sobrescrito.
                  </div>
                )}
              </>
            )}

            {/* Carteira: a regra e do SGA, nao de quem clicou. */}
            {carteira && carteira.podeImportar && !carteira.daMinhaCarteira && (
              <div className="importacao-sga-aviso importacao-sga-aviso-atencao">
                Este registro pertence a carteira do voluntario{' '}
                {carteira.nomeVoluntario || carteira.codigoVoluntario}. Ele continuara nessa
                carteira depois de importado.
              </div>
            )}

            {bloqueado && (
              <div className="importacao-sga-aviso importacao-sga-aviso-erro">
                {carteira?.motivoBloqueio}
              </div>
            )}

            {temBloqueantes && (
              <div className="importacao-sga-aviso importacao-sga-aviso-erro">
                O cadastro no SGA esta sem {preview.bloqueantes.join(' e ')}. Complete no SGA
                antes de importar.
              </div>
            )}
          </>
        )}

        <div className="importacao-sga-acoes">
          <button
            type="button"
            className="importacao-sga-botao importacao-sga-cancelar"
            onClick={onCancelar}
            disabled={importando}
          >
            {bloqueado || temBloqueantes ? 'Fechar' : 'Cancelar'}
          </button>
          <button
            type="button"
            className="importacao-sga-botao importacao-sga-confirmar"
            onClick={onConfirmar}
            disabled={!podeConfirmar}
          >
            {importando ? 'Importando...' : 'Importar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalImportacaoSga;
